import { useEffect, useLayoutEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { capturePageview } from '@lib/analytics/posthog-pageview';
import { resetOncePerPageAnalytics } from '@lib/analytics/once-per-page';
import type { ArticlePageviewProperties } from '@lib/analytics/article-pageview';

interface PostHogPageviewTrackerProps {
  hasAnalyticsConsent: boolean;
  /** True once the initial CookieYes read has completed (consent.hasChecked). */
  hasConsentBeenChecked: boolean;
  /** The current page's article properties; null off article pages. */
  articlePageviewProperties?: ArticlePageviewProperties | null;
}

/**
 * FFH-2370 — route-based pageview capture.
 *
 * Listens to the Next.js router rather than enabling the SDK's own history
 * listener (`capture_pageview: 'history_change'`) for two reasons:
 *
 * 1. The quiz funnels step via raw `window.history.pushState` hash updates
 *    (see `useQuizHashTracking`), which is exactly what inflates Datadog RUM
 *    to 6.6 views per session on /ai-team-rating. The SDK's history listener
 *    would count the same churn; the router listener never sees it, because
 *    those pushes bypass the router.
 * 2. The router hands us the route pattern (`/player/[slug]`), which is the
 *    property funnels and paths break down on without cardinality blowout.
 *
 * The landing pageview is deferred until the CookieYes consent settle: capture
 * is opted out until `setPostHogConsent(true)`, so capturing on mount would be
 * silently dropped and the funnel entry page lost. Route changes after that
 * are captured as they complete, with shallow changes (same-page state
 * updates) excluded — they are the SPA equivalent of the hash churn above.
 */
const PostHogPageviewTracker: React.FC<PostHogPageviewTrackerProps> = ({
  hasAnalyticsConsent,
  hasConsentBeenChecked,
  articlePageviewProperties = null
}) => {
  const router = useRouter();

  // FFH-3569: read at capture time. The router commits the new page's render,
  // props and all, before routeChangeComplete fires, and a layout effect runs
  // inside that commit, so the ref holds the destination's properties when the
  // handler runs. Written there rather than during render, so a render React
  // throws away can't leave another page's properties behind. A passive effect
  // could run after the event.
  const articlePageviewPropertiesRef = useRef(articlePageviewProperties);
  useLayoutEffect(() => {
    articlePageviewPropertiesRef.current = articlePageviewProperties;
  }, [articlePageviewProperties]);

  const isCaptureAllowed = hasConsentBeenChecked && hasAnalyticsConsent;
  // Read by the route-change handler, which outlives any single render.
  const isCaptureAllowedRef = useRef(isCaptureAllowed);
  isCaptureAllowedRef.current = isCaptureAllowed;

  // Whether this page load has sent a pageview yet. Guards the landing
  // capture so it fires exactly once, on whichever page the visitor is on
  // when consent settles — pre-settle navigations send nothing, so the first
  // allowed capture is always the current page.
  const hasCapturedPageviewRef = useRef(false);

  useEffect(() => {
    if (!isCaptureAllowed) return;
    if (hasCapturedPageviewRef.current) return;

    hasCapturedPageviewRef.current = true;
    capturePageview(router.pathname, articlePageviewPropertiesRef.current);
  }, [isCaptureAllowed, router.pathname]);

  useEffect(() => {
    const handleRouteChangeComplete = (
      _url: string,
      { shallow: isShallow }: { shallow: boolean }
    ): void => {
      if (isShallow) return;
      if (!isCaptureAllowedRef.current) return;

      hasCapturedPageviewRef.current = true;
      // The router mutates its shared instance before this event fires, so
      // `pathname` is the destination's route pattern, not the origin's.
      capturePageview(router.pathname, articlePageviewPropertiesRef.current);
    };

    // FFH-2372: a non-shallow navigation ends the page visit, so view-type
    // events guarded by the once-per-page module may fire again on the next
    // page. Reset on start, not complete, so nothing the new page mounts
    // during the transition is mistaken for a repeat of the old one. Runs
    // regardless of consent: the guard tracks visits, not captures.
    const handleRouteChangeStart = (
      _url: string,
      { shallow: isShallow }: { shallow: boolean }
    ): void => {
      if (isShallow) return;
      resetOncePerPageAnalytics();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  return null;
};

export default PostHogPageviewTracker;
